import { GROUPS_SORT_OPTIONS } from "@components/groups/groups-constants";
import { GROUPS_TABLE_COLUMN_LABELS } from "@components/table/groups/groups-table-constants";
import { MEMBERS_SORT_OPTIONS } from "@components/table/members/members-sort-constants";
import { MEMBERS_TABLE_COLUMN_LABELS } from "@components/table/members/members-table-constants";
import { TEAMS_TABLE_COLUMN_LABELS } from "@components/table/teams/teams-table-constants";
import { TEAMS_SORT_OPTIONS } from "@components/teams/teams-constants";
import { GROUPS_SORT_BY } from "@custom-types/group-types";
import { MEMBERS_SORT_BY } from "@custom-types/member-types";
import { TEAMS_SORT_BY } from "@custom-types/teams-types";
import { SNAPSHOTS_SORT_OPTIONS, SNAPSHOTS_TABLE_COLUMN_LABELS } from "@pages/project-details/project-snapshots/snapshots-table-constants";
import { SNAPSHOTS_SORT_BY } from "@pages/project-details/project-snapshots/snapshots-table-types";
import { FaroCoreApiSchemas } from "@stellar/core-api-client";
import { UtilsTypes } from "@stellar/utils";
const GROUPS_SORT_BY_SET = new Set(GROUPS_SORT_BY);
const TEAMS_SORT_BY_SET = new Set(TEAMS_SORT_BY);
const MEMBERS_SORT_BY_SET = new Set(MEMBERS_SORT_BY);
const SNAPSHOTS_SORT_BY_SET = new Set(SNAPSHOTS_SORT_BY);
const TEAM_MEMBERS_SORT_BY_SET = new Set(FaroCoreApiSchemas.teamMemberSortBySchema.options);

/** Determine whether the type is for details of a group */
export function isGroupDetails(group) {
  // Use the unique fields "members";
  return "members" in group;
}

/** Whether the project status is of type ProjectArchivingState */
export function isProjectArchivingState(projectStatus) {
  return projectStatus !== undefined && Object.values(UtilsTypes.ProjectArchivingState).includes(projectStatus);
}

/** Determine whether the provided image src is png or svg */
export function isSrcOfTypeString(src) {
  return typeof src === "string";
}

/** Whether the value is in type of string[] or not */
export function isStringArray(value) {
  return Array.isArray(value) &&
  // value.every returns true for an empty array. So it needs to be checked separately
  value.length > 0 && value.every(item => typeof item === "string");
}

/** @returns Whether the value is a FileSystemDirectoryHandle. */
export function isFileSystemDirectoryHandle(value) {
  return (value.kind === "directory" || value.isDirectory) && value.entries instanceof Function;
}

/** @returns Whether the value is a FileSystemDirectoryEntry. */
export function isFileSystemDirectoryEntry(value) {
  return value.isDirectory && value.createReader instanceof Function;
}

/** @returns Whether the value is a FileSystemFileHandle. */
export function isFileSystemFileHandle(value) {
  return (value.kind === "file" || value.isFile) && value.getFile instanceof Function;
}

/** @returns Whether the value is a FileSystemFileEntry. */
export function isFileSystemFileEntry(value) {
  return value.isFile && value.file instanceof Function;
}

/**
 * Type guard to check if a string is a valid GroupsSortField
 */
export function isGroupsSortField(field) {
  return field in GROUPS_SORT_OPTIONS;
}

/** Type guard to check if a string is a valid SnapshotsSortField */
export function isSnapshotsSortField(field) {
  return field in SNAPSHOTS_SORT_OPTIONS;
}

/** Type guard to check if a value is a valid SnapshotsSortBy */
export function isSnapshotsSortBy(value) {
  if (!value) {
    return false;
  }
  return SNAPSHOTS_SORT_BY_SET.has(value);
}

/** Type guard to check if a string is a valid SnapshotsTableColumnField */
export function isSnapshotsTableColumnField(key) {
  return key in SNAPSHOTS_TABLE_COLUMN_LABELS;
}

/**
 * Type guard to check if a value is a valid GroupsSortBy
 */
export function isGroupsSortBy(value) {
  if (!value) {
    return false;
  }
  return GROUPS_SORT_BY_SET.has(value);
}

/** Type guard to check if a string is a valid GroupsTableColumnField */
export function isGroupsTableColumnField(key) {
  return key in GROUPS_TABLE_COLUMN_LABELS;
}

/**
 * Type guard to check if a string is a valid MembersTableColumnField value.
 * Used to safely filter and map column fields from user preferences.
 */
export function isMembersTableColumnField(field) {
  return Object.keys(MEMBERS_TABLE_COLUMN_LABELS).includes(field);
}

/** Type guard to check if a string is a valid TeamsTableColumnField */
export function isTeamsTableColumnField(key) {
  return key in TEAMS_TABLE_COLUMN_LABELS;
}

/**
 * Type guard to check if a value is a valid TeamsSortBy
 */
export function isTeamsSortBy(value) {
  if (!value) {
    return false;
  }
  return TEAMS_SORT_BY_SET.has(value);
}

/**
 * Type guard to check if a string is a valid TeamsSortField
 */
export function isTeamsSortField(field) {
  return field in TEAMS_SORT_OPTIONS;
}

/**
 * Type guard to check if a value is a valid MembersSortBy
 */
export function isMembersSortBy(value) {
  if (!value) {
    return false;
  }
  return MEMBERS_SORT_BY_SET.has(value);
}

/**
 * Type guard to check if a string is a valid MembersSortField
 */
export function isMembersSortField(field) {
  return field in MEMBERS_SORT_OPTIONS;
}

/**
 * Type guard to check if a value is a valid TeamMemberSortBy
 */
export function isTeamMembersSortField(value) {
  if (!value) {
    return false;
  }
  return TEAM_MEMBERS_SORT_BY_SET.has(value);
}